import React, { useState } from "react";
import Drawer from "../../components/drawer/drawer";
import Modal from "../../components/modal/modal";
import PageHeader from "../../components/page-header/page-header";
import { usePagination } from "../../hooks/use-pagination";
import { VideoReview } from "../../types";
import { VideoReviewManagementState } from "./types/video-reviews-types";
import { VideoReviewForm } from "./components/video-review-form";
import { VideoReviewTable } from "./components/video-review-table";
import { useVideoReviews } from "./hooks/use-video-reviews";
import {
  resetVideoReviewState,
  openEditDrawer,
  openCreateDrawer,
  closeDrawer,
  openDeleteModal,
  closeDeleteModal,
  getDrawerTitle,
  getVideoReviewDisplayName,
} from "./utils/video-reviews-utils";
import "./video-review.css";
import { toast } from "sonner";

const VideoReviewPage: React.FC = () => {
  const [state, setState] = useState<VideoReviewManagementState>(
    resetVideoReviewState()
  );

  const {
    reviews,
    isLoading,
    handleCreateVideoReview,
    handleUpdateVideoReview,
    handleDeleteVideoReview,
  } = useVideoReviews();

  const {
    currentPage,
    itemsPerPage,
    paginatedItems,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(reviews);

  const handleSubmit = async (values: Partial<VideoReview>): Promise<void> => {
    let response: any;
    if (state.editingReview) {
      response = await handleUpdateVideoReview(state.editingReview.id, values);
    } else {
      response = await handleCreateVideoReview(values);
    }

    const hasSuccessField = typeof response?.success === "boolean";
    if (!hasSuccessField || response?.success) {
      toast.success(response?.message || "Операция выполнена успешно");
      closeDrawer(setState);
    } else {
      toast.error(response?.message || "Произошла ошибка");
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!state.deletingReview) return;

    const success = await handleDeleteVideoReview(state.deletingReview.id);
    if (success) {
      closeDeleteModal(setState);
    }
  };

  const handleEdit = (review: VideoReview): void => {
    openEditDrawer(setState, review);
  };

  const handleCreate = (): void => {
    openCreateDrawer(setState);
  };

  const handleCancel = (): void => {
    closeDrawer(setState);
  };

  const handleDeleteClick = (review: VideoReview): void => {
    openDeleteModal(setState, review);
  };

  const handleDeleteCancel = (): void => {
    closeDeleteModal(setState);
  };

  return (
    <div className="video-review">
      <PageHeader
        title="Видеоотзывы"
        subtitle="Управление видеоотзывами клиентов"
        actionButton={{
          text: "Добавить видеоотзыв",
          icon: "plus",
          onClick: handleCreate,
        }}
      />

      <VideoReviewTable
        reviews={paginatedItems}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <Drawer
        title={getDrawerTitle(state.editingReview)}
        isOpen={state.isDrawerOpen}
        onClose={handleCancel}
        width={700}
      >
        <VideoReviewForm
          initialValues={state.editingReview}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
        />
      </Drawer>

      <Modal
        isOpen={state.isDeleteModalOpen}
        onClose={handleDeleteCancel}
        title="Вы уверены, что хотите удалить этот видеоотзыв?"
        onConfirm={handleDelete}
      >
        <p>Вы уверены, что хотите удалить этот видеоотзыв?</p>
        <p>
          <strong>
            {state.deletingReview
              ? getVideoReviewDisplayName(state.deletingReview)
              : ""}
          </strong>
        </p>
      </Modal>
    </div>
  );
};

export default VideoReviewPage;
